import { useAuthStore } from '@/stores/authStore';
/**
 * SSL Wide Watch API client. The SB3 backend serves all endpoints under
 * `/admin/**` (staff plane). A bearer token in the `Authorization` header
 * is required on every authenticated call after /admin/session/exchange.
 *
 * Default API_BASE_URL is the empty string — calls go same-origin against
 * the SPA's own host (`staging.arthur.wide.watch`). The /admin/* path on
 * that host is handled by a Cloudflare Pages Function (functions/admin/
 * [[path]].ts) that proxies to the SSL Wide Watch backend at ssl.wide.watch
 * with the Cf-Access-Jwt-Assertion header preserved. This sidesteps the
 * cross-origin SSO bootstrap problem (browser can't carry a CF Access JWT
 * cross-origin to a hostname Cloudflare doesn't proxy).
 *
 * Override via VITE_API_BASE_URL only for local-dev (e.g. point at a
 * tunnel or a backend running on localhost).
 *
 * A 401 anywhere clears the auth store; the router's <RequireAuth />
 * guard then kicks the user back to /signin on next render.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
export class ApiError extends Error {
    status;
    code;
    constructor(message, status, code) {
        super(message);
        this.status = status;
        this.code = code;
    }
    get is4xx() {
        return this.status >= 400 && this.status < 500;
    }
    get is5xx() {
        return this.status >= 500;
    }
    get isTransient() {
        return this.is5xx || this.status === 429;
    }
}
/**
 * Decode a JWT's `exp` (seconds since epoch) and report whether it has passed.
 * Used to tell an expired staff session apart from a genuine role denial: an
 * expired staff JWT comes back from the backend as 403, same as an
 * authorization failure, so status alone can't distinguish them.
 */
function isJwtExpired(token, skewSeconds = 10) {
    try {
        const payload = token.split('.')[1];
        if (!payload)
            return false;
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        if (typeof json.exp !== 'number')
            return false;
        return Date.now() / 1000 >= json.exp - skewSeconds;
    }
    catch {
        return false; // unparseable — let the server be the judge
    }
}
/**
 * Tear down an expired/invalid staff session and bounce to login.
 *
 * Clearing the store isn't enough on its own: <AccessHandshake> runs the
 * Cloudflare Access exchange exactly once per page load (a `ran` ref guards
 * it), so a mid-session token clear would strand the user on the "exchanging"
 * state. A full reload re-runs the handshake — silently re-exchanging the CF
 * Access cookie for a fresh staff JWT when it's still valid, or showing the
 * SSO prompt when it isn't. A short sessionStorage guard prevents reload loops.
 */
function endSession() {
    const { accessToken, clearAuth } = useAuthStore.getState();
    if (accessToken)
        clearAuth();
    if (typeof window === 'undefined')
        return;
    const now = Date.now();
    const last = Number(sessionStorage.getItem('arthur:sessionReloadAt') ?? 0);
    if (now - last < 15000)
        return; // already bounced very recently — don't loop
    sessionStorage.setItem('arthur:sessionReloadAt', String(now));
    window.location.assign('/');
}
function buildUrl(path, query) {
    const base = API_BASE_URL.replace(/\/$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const url = `${base}${normalizedPath}`;
    if (!query)
        return url;
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
        if (v === undefined || v === null)
            continue;
        params.set(k, String(v));
    }
    const qs = params.toString();
    return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
}
async function request(method, path, body, opts = {}) {
    const url = buildUrl(path, opts.query);
    const headers = new Headers({ Accept: opts.accept ?? 'application/json' });
    if (!opts.anonymous) {
        const token = useAuthStore.getState().accessToken;
        // Proactively bounce an already-expired staff JWT instead of firing a
        // burst of doomed requests that each render "Access Denied".
        if (token && isJwtExpired(token)) {
            endSession();
            throw new ApiError('Session expired', 401);
        }
        if (token)
            headers.set('Authorization', `Bearer ${token}`);
    }
    let initBody;
    if (opts.rawBody !== undefined) {
        initBody = opts.rawBody;
    }
    else if (body !== undefined) {
        headers.set('Content-Type', 'application/json');
        initBody = JSON.stringify(body);
    }
    const res = await fetch(url, { method, headers, body: initBody });
    if (!opts.anonymous && (res.status === 401 || res.status === 403)) {
        // 401 is always an auth failure. An expired/invalid staff JWT is rejected
        // by the backend's @PreAuthorize layer as 403 — so a 403 is *also* an auth
        // failure when our own token is missing or expired. A 403 with a still-valid
        // token is a genuine role denial (e.g. support hitting a billing endpoint)
        // and must NOT sign the user out — it falls through to the error path below
        // so the widget can show "Access Denied" without bouncing the session.
        const token = useAuthStore.getState().accessToken;
        const sessionInvalid = res.status === 401 || !token || isJwtExpired(token);
        if (sessionInvalid) {
            endSession();
            throw new ApiError('Session expired', res.status);
        }
    }
    if (!res.ok) {
        let code;
        let message = res.statusText || 'Request failed';
        try {
            const payload = (await res.clone().json());
            code = payload.error?.code;
            message = payload.error?.message ?? payload.message ?? message;
        }
        catch {
            /* non-JSON body */
        }
        throw new ApiError(message, res.status, code);
    }
    if (res.status === 204)
        return undefined;
    const contentType = res.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
        return (await res.text());
    }
    return (await res.json());
}
export const apiClient = {
    get: (path, opts) => request('GET', path, undefined, opts),
    post: (path, body, opts) => request('POST', path, body, opts),
    put: (path, body, opts) => request('PUT', path, body, opts),
    patch: (path, body, opts) => request('PATCH', path, body, opts),
    delete: (path, opts) => request('DELETE', path, undefined, opts),
};
